/* Clasio theme — core scripts */
(() => {
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };
  const fetchJSON = (url, opts = {}) =>
    fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, ...opts });

  const root = () => (window.routes && window.routes.root) || '/';
  const cartSections = () => {
    const ids = ['cart-icon-bubble'];
    if (qs('cart-drawer')) ids.push('cart-drawer');
    if (qs('#main-cart-items')) ids.push(qs('#main-cart-items').dataset.section);
    if (qs('#main-cart-footer')) ids.push(qs('#main-cart-footer').dataset.section);
    return ids;
  };

  /* ---------- Section rendering helpers ---------- */
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  const renderCartSections = (sections) => {
    if (!sections) return;
    if (sections['cart-icon-bubble']) {
      const fresh = qs('#cart-icon-bubble', parse(sections['cart-icon-bubble']));
      qsa('#cart-icon-bubble').forEach((el) => fresh && el.replaceWith(fresh.cloneNode(true)));
    }
    if (sections['cart-drawer']) {
      const drawer = qs('cart-drawer');
      const fresh = qs('cart-drawer', parse(sections['cart-drawer']));
      if (drawer && fresh) {
        drawer.innerHTML = fresh.innerHTML;
        drawer.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
      }
    }
    const page = qs('#main-cart-items');
    if (page && sections[page.dataset.section]) {
      const fresh = qs('#main-cart-items', parse(sections[page.dataset.section]));
      if (fresh) page.innerHTML = fresh.innerHTML;
      page.classList.toggle('is-empty', !!(fresh && fresh.classList.contains('is-empty')));
    }
    const footer = qs('#main-cart-footer');
    if (footer && sections[footer.dataset.section] !== undefined) {
      const fresh = qs('#main-cart-footer', parse(sections[footer.dataset.section]));
      if (fresh) footer.innerHTML = fresh.innerHTML;
      else footer.innerHTML = '';
    }
  };

  const refreshCart = async () => {
    try {
      const res = await fetch(`${root()}?sections=${cartSections().join(',')}`, { credentials: 'same-origin' });
      if (res.ok) renderCartSections(await res.json());
    } catch (e) { /* ignore */ }
  };

  const updateLine = async (line, quantity) => {
    const res = await fetchJSON(window.routes.cart_change_url, {
      method: 'POST',
      body: JSON.stringify({ line, quantity, sections: cartSections(), sections_url: window.location.pathname })
    });
    const data = await res.json();
    if (data.status || data.errors) throw new Error(data.description || data.message || window.themeStrings.cartError);
    renderCartSections(data.sections);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    return data;
  };

  /* ---------- Body scroll lock ---------- */
  let lockCount = 0;
  const lockScroll = () => { lockCount += 1; document.documentElement.classList.add('scroll-locked'); };
  const unlockScroll = () => { lockCount = Math.max(0, lockCount - 1); if (!lockCount) document.documentElement.classList.remove('scroll-locked'); };

  /* ---------- Drawers ---------- */
  class DrawerBase extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); this.close(); } });
      this.addEventListener('keyup', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open(trigger) {
      if (this.classList.contains('is-open')) return;
      this.trigger = trigger || document.activeElement;
      this.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => this.classList.add('is-open'));
      lockScroll();
      const panel = qs('.drawer__panel', this);
      if (panel) setTimeout(() => panel.focus({ preventScroll: true }), 50);
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'true');
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
      if (this.trigger && this.trigger.setAttribute) { this.trigger.setAttribute('aria-expanded', 'false'); this.trigger.focus && this.trigger.focus({ preventScroll: true }); }
    }
  }
  customElements.define('menu-drawer', class extends DrawerBase {});

  class CartDrawer extends DrawerBase {
    connectedCallback() {
      super.connectedCallback();
      bindCartControls(this);
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  // Generic drawer opener (header menu, facets)
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      const target = document.getElementById(opener.dataset.drawerOpen);
      if (target) {
        e.preventDefault();
        if (typeof target.open === 'function') target.open(opener);
        else openPlainDrawer(target, opener);
      }
      return;
    }
    const cartOpener = e.target.closest('[data-cart-open]');
    if (cartOpener) {
      const drawer = qs('cart-drawer');
      if (drawer && !document.documentElement.classList.contains('is-fast-add-frame')) { e.preventDefault(); drawer.open(cartOpener); }
    }
  });

  // Facets drawer is a plain element (lives inside a form)
  const openPlainDrawer = (el, trigger) => {
    el.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => el.classList.add('is-open'));
    lockScroll();
    const close = (ev) => {
      if (ev.type === 'keyup' && ev.key !== 'Escape') return;
      if (ev.type === 'click' && !ev.target.closest('[data-drawer-close]')) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      unlockScroll();
      el.removeEventListener('click', close);
      el.removeEventListener('keyup', close);
      trigger && trigger.focus && trigger.focus();
    };
    el.addEventListener('click', close);
    el.addEventListener('keyup', close);
  };

  document.addEventListener('cart:open', () => { const d = qs('cart-drawer'); d && d.open(); });
  document.addEventListener('cart:refresh', refreshCart);

  /* ---------- Quantity input ---------- */
  customElements.define('quantity-input', class extends HTMLElement {
    connectedCallback() {
      this.input = qs('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;
        e.preventDefault();
        const prev = Number(this.input.value) || 0;
        const min = Number(this.input.min) || 0;
        const next = btn.name === 'plus' ? prev + 1 : Math.max(min, prev - 1);
        if (next === prev) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  });

  /* ---------- Cart controls (drawer + page) ---------- */
  function bindCartControls(scope) {
    const onChange = debounce(async (input) => {
      const line = Number(input.dataset.line);
      const item = input.closest('.cart-item');
      scope.classList.add('is-loading');
      try {
        await updateLine(line, Number(input.value));
      } catch (err) {
        const box = item && qs('.cart-item__error', item);
        if (box) { box.textContent = err.message; box.hidden = false; }
      } finally {
        scope.classList.remove('is-loading');
      }
    }, 350);

    scope.addEventListener('change', (e) => {
      if (e.target.matches('.cart-item .quantity__input')) onChange(e.target);
      if (e.target.matches('[data-cart-note]')) {
        fetchJSON(window.routes.cart_update_url, { method: 'POST', body: JSON.stringify({ note: e.target.value }) });
      }
    });
    scope.addEventListener('click', async (e) => {
      const remove = e.target.closest('[data-cart-remove]');
      if (!remove) return;
      e.preventDefault();
      scope.classList.add('is-loading');
      try { await updateLine(Number(remove.dataset.cartRemove), 0); } catch (err) { /* noop */ }
      scope.classList.remove('is-loading');
    });
  }

  customElements.define('cart-page', class extends HTMLElement {
    connectedCallback() { bindCartControls(this); }
  });

  // Cart page footer: discount field + note
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-discount-form]');
    if (!form) return;
    e.preventDefault();
    const input = qs('input[name="discount"]', form);
    const code = input.value.trim();
    if (!code) { input.setCustomValidity(input.dataset.error || ''); input.reportValidity(); input.setCustomValidity(''); return; }
    window.location.href = `${root()}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(window.routes.cart_url)}`;
  });
  document.addEventListener('change', (e) => {
    if (e.target.matches('#main-cart-footer [data-cart-note]')) {
      fetchJSON(window.routes.cart_update_url, { method: 'POST', body: JSON.stringify({ note: e.target.value }) });
    }
  });

  /* ---------- Product form ---------- */
  customElements.define('product-form', class extends HTMLElement {
    connectedCallback() {
      this.form = qs('form', this);
      if (!this.form) return;
      this.submitBtn = qs('[type="submit"]', this.form);
      this.errorBox = qs('.product-form__error', this.form);
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      if (this.dataset.skipCart === 'true') {
        e.preventDefault();
        const id = qs('[name="id"]', this.form).value;
        const qtyInput = this.form.querySelector('[name="quantity"]') || document.querySelector(`[form="${this.form.id}"][name="quantity"]`);
        const qty = qtyInput ? qtyInput.value : 1;
        window.location.href = `${root()}cart/${id}:${qty}`;
        return;
      }
      const drawer = qs('cart-drawer');
      const inFrame = document.documentElement.classList.contains('is-fast-add-frame');
      if (!drawer && !inFrame && window.themeSettings.cartType !== 'drawer') return; // native submit → /cart
      e.preventDefault();
      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;
      this.setLoading(true);
      this.showError('');

      const fd = new FormData(this.form);
      // quantity input may live outside the form via form="" attribute (already included by FormData)
      if (!inFrame) fd.append('sections', cartSections().join(','));
      fd.append('sections_url', window.location.pathname);

      try {
        const res = await fetch(window.routes.cart_add_url, {
          method: 'POST', body: fd, credentials: 'same-origin',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }
        });
        const data = await res.json();
        if (data.status || data.errors) { this.showError((res.status === 422 && (data.description || data.message)) || window.themeStrings.cartError); return; }
        document.dispatchEvent(new CustomEvent('product:added', { detail: data }));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
        if (inFrame) return; // parent window watches the cart and closes the dialog
        renderCartSections(data.sections);
        if (drawer) drawer.open(this.submitBtn);
        else window.location.href = window.routes.cart_url;
      } catch (err) {
        this.showError(window.themeStrings.cartError);
      } finally {
        this.setLoading(false);
      }
    }
    setLoading(on) {
      this.submitBtn.classList.toggle('is-loading', on);
      this.submitBtn.setAttribute('aria-disabled', on ? 'true' : 'false');
      const spinner = qs('.loading-spinner', this.submitBtn);
      if (spinner) spinner.hidden = !on;
    }
    showError(msg) {
      if (!this.errorBox) return;
      this.errorBox.textContent = msg;
      this.errorBox.hidden = !msg;
    }
  });

  /* ---------- Variant picker ---------- */
  customElements.define('variant-picker', class extends HTMLElement {
    connectedCallback() {
      try { this.variants = JSON.parse(qs('[data-variants]', this).textContent); } catch (e) { this.variants = []; }
      this.addEventListener('change', () => this.onChange());
    }
    selectedOptions() {
      const groups = qsa('.variant-picker__group', this);
      return groups.map((g) => {
        const sel = qs('select', g);
        if (sel) return sel.value;
        const checked = qs('input:checked', g);
        return checked ? checked.value : null;
      });
    }
    onChange() {
      const opts = this.selectedOptions();
      qsa('.variant-picker__group', this).forEach((g, i) => { const v = qs('[data-option-value]', g); if (v) v.textContent = opts[i]; });
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      const form = document.getElementById(this.dataset.form);
      const btn = form && qs('[type="submit"]', form);
      const label = btn && qs('[data-add-label]', btn);
      if (!variant) {
        if (btn) btn.disabled = true;
        if (label) label.textContent = window.themeStrings.unavailable;
        return;
      }
      if (form) { const idInput = qs('[name="id"]', form); idInput.value = variant.id; idInput.dispatchEvent(new Event('change', { bubbles: true })); }
      if (btn) btn.disabled = !variant.available;
      if (label) label.textContent = variant.available ? window.themeStrings.addToCart : window.themeStrings.soldOut;
      const url = `${this.dataset.url}?variant=${variant.id}`;
      window.history.replaceState({}, '', url);
      // refresh price block and gallery from server-rendered section
      fetch(`${url}&section_id=${this.dataset.section}`)
        .then((r) => r.text())
        .then((html) => {
          const doc = parse(html);
          const sid = this.dataset.section;
          const fresh = qs(`#Price-${sid}`, doc);
          const cur = document.getElementById(`Price-${sid}`);
          if (fresh && cur) cur.innerHTML = fresh.innerHTML;
          const btnPrice = btn && qs('[data-btn-price]', btn);
          const freshBtnPrice = qs('[data-btn-price]', doc);
          if (btnPrice && freshBtnPrice) btnPrice.innerHTML = freshBtnPrice.innerHTML;
        });
      if (variant.featured_media) {
        const gallery = qs(`media-gallery[data-section="${this.dataset.section}"]`);
        if (gallery) gallery.goToMedia(variant.featured_media.id);
      }
    }
  });

  /* ---------- Media gallery ---------- */
  customElements.define('media-gallery', class extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-gallery-track]', this);
      this.slides = qsa('.product-gallery__slide', this);
      this.thumbs = qsa('[data-gallery-thumb]', this);
      this.dots = qsa('.product-gallery__dot', this);
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.galleryThumb))));
      const prev = qs('[data-gallery-prev]', this);
      const next = qs('[data-gallery-next]', this);
      prev && prev.addEventListener('click', () => this.go(this.index - 1));
      next && next.addEventListener('click', () => this.go(this.index + 1));
      this.track.addEventListener('scroll', debounce(() => {
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (i !== this.index) this.setActive(i, false);
      }, 80), { passive: true });
      if (this.index > 0) requestAnimationFrame(() => this.go(this.index, 'auto'));
    }
    go(i, behavior = 'smooth') {
      const n = this.slides.length;
      if (!n) return;
      i = (i + n) % n;
      this.track.scrollTo({ left: this.slides[i].offsetLeft, behavior });
      this.setActive(i, true);
    }
    setActive(i) {
      this.index = i;
      this.slides.forEach((s, k) => s.classList.toggle('is-active', k === i));
      this.thumbs.forEach((t, k) => t.classList.toggle('is-active', k === i));
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
      const active = this.thumbs[i];
      if (active) active.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      qsa('video', this).forEach((v, k) => { if (!this.slides[i].contains(v)) v.pause(); });
    }
    goToMedia(id) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i > -1) this.go(i);
    }
  });

  /* ---------- Generic slider row ---------- */
  customElements.define('slider-row', class extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-slider-track]', this);
      this.prev = qs('[data-slider-prev]', this);
      this.next = qs('[data-slider-next]', this);
      if (!this.track) return;
      const step = () => {
        const item = this.track.firstElementChild;
        if (!item) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return item.getBoundingClientRect().width + gap;
      };
      this.prev && this.prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      this.next && this.next.addEventListener('click', () => {
        const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;
        this.track.scrollTo({ left: atEnd ? 0 : this.track.scrollLeft + step(), behavior: 'smooth' });
      });
      const update = () => {
        const scrollable = this.track.scrollWidth > this.track.clientWidth + 4;
        this.classList.toggle('is-scrollable', scrollable);
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
        if (this.next) this.next.disabled = !scrollable;
      };
      this.track.addEventListener('scroll', debounce(update, 60), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
      const auto = Number(this.dataset.autoplay);
      if (auto > 0) {
        let paused = false;
        this.addEventListener('mouseenter', () => { paused = true; });
        this.addEventListener('mouseleave', () => { paused = false; });
        this.addEventListener('touchstart', () => { paused = true; }, { passive: true });
        setInterval(() => { if (!paused && this.classList.contains('is-scrollable')) this.next && this.next.click(); }, auto * 1000);
      }
    }
  });

  /* ---------- Sticky header ---------- */
  customElements.define('sticky-header', class extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode;
      this.sectionEl = this.closest('.shopify-section') || this;
      document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
      if (this.mode !== 'on-scroll-up') return;
      let last = 0;
      window.addEventListener('scroll', () => {
        const y = window.scrollY;
        const h = this.offsetHeight;
        if (y > last && y > h + 100) this.sectionEl.classList.add('header-hidden');
        else this.sectionEl.classList.remove('header-hidden');
        this.sectionEl.classList.toggle('header-scrolled', y > 0);
        last = y;
      }, { passive: true });
      // search toggles
    }
  });

  /* ---------- Header search ---------- */
  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-search-open]');
    const close = e.target.closest('[data-search-close]');
    const panel = document.getElementById('HeaderSearch');
    if (!panel) return;
    if (open) {
      e.preventDefault();
      panel.hidden = !panel.hidden;
      if (!panel.hidden) { const input = qs('input[type="search"]', panel); input && input.focus(); }
    } else if (close) {
      panel.hidden = true;
    }
  });

  /* ---------- Predictive search ---------- */
  customElements.define('predictive-search', class extends HTMLElement {
    connectedCallback() {
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-predictive-results]', this);
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', debounce(() => this.search(), 280));
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.hide(); });
      this.input.addEventListener('keyup', (e) => { if (e.key === 'Escape') this.hide(); });
    }
    async search() {
      const q = this.input.value.trim();
      if (!q) return this.hide();
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,collection,query&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,vendor,tag`;
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) return this.hide();
        const doc = parse(await res.text());
        const content = qs('[data-predictive-content]', doc) || qs('#shopify-section-predictive-search', doc);
        this.results.innerHTML = content ? content.outerHTML : '';
        this.results.hidden = !content;
        this.input.setAttribute('aria-expanded', String(!!content));
      } catch (e) { /* aborted */ }
    }
    hide() { this.results.hidden = true; this.input.setAttribute('aria-expanded', 'false'); }
  });

  /* ---------- Facet filters ---------- */
  customElements.define('facet-filters', class extends HTMLElement {
    connectedCallback() {
      this.form = qs('[data-facet-form]', this);
      if (!this.form) return;
      const run = debounce(() => this.apply(), 450);
      this.form.addEventListener('change', (e) => { if (e.target.matches('[data-facet-input]')) run(); });
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.apply(); });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (!link) return;
        e.preventDefault();
        this.load(link.href);
      });
    }
    apply() {
      const params = new URLSearchParams(new FormData(this.form));
      for (const [k, v] of Array.from(params.entries())) if (v === '') params.delete(k);
      const url = `${this.form.action.split('?')[0]}?${params.toString()}`;
      this.load(url);
    }
    async load(url) {
      const section = this.dataset.section;
      const container = this.closest('[data-section]');
      container && container.classList.add('is-loading');
      const sep = url.includes('?') ? '&' : '?';
      try {
        const html = await fetch(`${url}${sep}section_id=${section}`).then((r) => r.text());
        const doc = parse(html);
        const freshResults = qs('[data-facet-results]', doc);
        const curResults = container && qs('[data-facet-results]', container);
        if (freshResults && curResults) curResults.innerHTML = freshResults.innerHTML;
        // update facets bar + active pills (keep drawer open state)
        const freshForm = qs('[data-facet-form]', doc);
        if (freshForm) {
          const wasOpen = qs('.facets__drawer.is-open', this);
          ['.facets__bar', '.facets__active'].forEach((sel) => {
            const a = qs(sel, this.form); const b = qs(sel, freshForm);
            if (a && b) a.replaceWith(b); else if (a && !b) a.remove(); else if (!a && b) qs('.facets__bar', this.form).after(b);
          });
          if (!wasOpen) { const d = qs('.facets__drawer', this.form); const fd = qs('.facets__drawer', freshForm); if (d && fd) d.replaceWith(fd); }
        }
        window.history.replaceState({}, '', url);
      } finally {
        container && container.classList.remove('is-loading');
      }
    }
  });

  /* ---------- Product recommendations ---------- */
  customElements.define('product-recommendations', class extends HTMLElement {
    connectedCallback() {
      if (this.children.length) return;
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        fetch(this.dataset.url).then((r) => r.text()).then((html) => {
          const fresh = qs('product-recommendations', parse(html));
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
        });
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  });

  /* ---------- Stories ---------- */
  customElements.define('insta-stories', class extends HTMLElement {
    connectedCallback() {
      this.viewer = qs('[data-story-viewer]', this);
      this.duration = (Number(this.dataset.duration) || 8) * 1000;
      this.muted = true;
      if (!this.viewer) return;
      document.body.appendChild(this.viewer); // escape stacking contexts
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-story-open]');
        if (btn) this.openUser(Number(btn.dataset.storyOpen), 0);
      });
      this.viewer.addEventListener('click', (e) => {
        if (e.target.closest('[data-story-close]')) return this.close();
        if (e.target.closest('[data-story-next]')) return this.step(1);
        if (e.target.closest('[data-story-prev]')) return this.step(-1);
        if (e.target.closest('[data-story-mute]')) {
          this.muted = !this.muted;
          e.target.closest('[data-story-mute]').textContent = this.muted ? '🔇' : '🔊';
          const v = this.currentMedia(); if (v && v.tagName === 'VIDEO') v.muted = this.muted;
        }
      });
      this.onKey = (e) => {
        if (this.viewer.hidden) return;
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowRight') this.step(1);
        if (e.key === 'ArrowLeft') this.step(-1);
      };
      document.addEventListener('keyup', this.onKey);
    }
    users() { return qsa('[data-story-user]', this.viewer); }
    currentMedia() { const s = this.slides && this.slides[this.slide]; return s && qs('.story-viewer__media', s); }
    openUser(u, s) {
      const users = this.users();
      if (u < 0 || u >= users.length) return this.close();
      users.forEach((el, i) => { el.hidden = i !== u; });
      this.user = u;
      this.slides = qsa('[data-story-slide]', users[u]);
      this.bars = qsa('.story-viewer__progress i', users[u]);
      if (!this.slides.length) return this.openUser(u + 1, 0);
      if (this.viewer.hidden) { this.viewer.hidden = false; lockScroll(); }
      this.show(s < 0 ? this.slides.length - 1 : s);
    }
    show(i) {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.raf);
      qsa('video', this.viewer).forEach((v) => { v.pause(); });
      this.slide = i;
      this.slides.forEach((el, k) => { el.hidden = k !== i; });
      this.bars.forEach((b, k) => { b.style.width = k < i ? '100%' : '0%'; });
      const time = qs('[data-story-time]', this.users()[this.user]);
      if (time) time.textContent = this.slides[i].dataset.time || '';
      const media = this.currentMedia();
      const bar = this.bars[i];
      if (media && media.tagName === 'VIDEO') {
        media.muted = this.muted;
        media.currentTime = 0;
        media.play().catch(() => {});
        const tick = () => {
          if (media.duration && bar) bar.style.width = `${(media.currentTime / media.duration) * 100}%`;
          if (!media.ended) this.raf = requestAnimationFrame(tick);
        };
        this.raf = requestAnimationFrame(tick);
        media.onended = () => this.step(1);
      } else {
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min(1, (now - start) / this.duration);
          if (bar) bar.style.width = `${p * 100}%`;
          if (p < 1) this.raf = requestAnimationFrame(tick); else this.step(1);
        };
        this.raf = requestAnimationFrame(tick);
      }
    }
    step(d) {
      const n = this.slide + d;
      if (n >= this.slides.length) return this.openUser(this.user + 1, 0);
      if (n < 0) return this.user > 0 ? this.openUser(this.user - 1, -1) : this.show(0);
      this.show(n);
    }
    close() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.raf);
      qsa('video', this.viewer).forEach((v) => v.pause());
      if (!this.viewer.hidden) { this.viewer.hidden = true; unlockScroll(); }
    }
  });

  /* ---------- Scroll to top ---------- */
  customElements.define('scroll-top-button', class extends HTMLElement {
    connectedCallback() {
      const offset = Number(this.dataset.offset) || 400;
      const update = () => this.classList.toggle('is-visible', window.scrollY > offset);
      window.addEventListener('scroll', debounce(update, 50), { passive: true });
      update();
      this.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
    }
  });

  /* ---------- Share ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share-url]');
    if (!btn) return;
    const url = btn.dataset.shareUrl;
    if (navigator.share) { try { await navigator.share({ url, title: btn.dataset.shareTitle }); } catch (err) { /* cancelled */ } return; }
    try { await navigator.clipboard.writeText(url); btn.classList.add('is-copied'); setTimeout(() => btn.classList.remove('is-copied'), 1500); } catch (err) { window.prompt('', url); }
  });

  /* ---------- Customer login/recover toggle ---------- */
  const syncCustomerPanels = () => {
    const recover = document.getElementById('recover');
    const login = document.getElementById('login');
    if (!recover || !login) return;
    const showRecover = window.location.hash === '#recover';
    recover.hidden = !showRecover;
    login.hidden = showRecover;
  };
  window.addEventListener('hashchange', syncCustomerPanels);
  document.addEventListener('DOMContentLoaded', syncCustomerPanels);

  /* ---------- Address country/province defaults ---------- */
  document.addEventListener('DOMContentLoaded', () => {
    qsa('select[data-default]').forEach((s) => { if (s.dataset.default) s.value = s.dataset.default; });
  });

  /* ---------- Scroll-in animations ---------- */
  if (document.body.classList.contains('has-animations') && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-shown'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -40px 0px' });
    const observe = (rootEl = document) => qsa('.animate-item:not(.is-shown)', rootEl).forEach((el) => io.observe(el));
    document.addEventListener('DOMContentLoaded', () => observe());
    const mo = new MutationObserver((muts) => muts.forEach((m) => m.addedNodes.forEach((n) => { if (n.nodeType === 1) observe(n); })));
    document.addEventListener('DOMContentLoaded', () => mo.observe(document.body, { childList: true, subtree: true }));
    if (window.Shopify && Shopify.designMode) document.documentElement.classList.add('no-animate');
  }

  /* ---------- Theme editor ---------- */
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:select', (e) => {
      if (e.target.querySelector('cart-drawer')) qs('cart-drawer').open();
    });
    document.addEventListener('shopify:section:deselect', (e) => {
      if (e.target.querySelector('cart-drawer')) qs('cart-drawer').close();
    });
  }
})();
